import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap, catchError } from 'rxjs';
import {
  ApiResponse,
  Broadcast,
} from '../../features/broadcast-config/broadcast';
import { ErrorHandlingService } from './error-handling.service';
import { HttpParams } from '@angular/common/http';

export interface QueryParams {
  organizationId?: number;
  pageNumber?: number;
  pageSize?: number;
  sortField?: string | null;
  sortOrder?: string | null;
  searchTerm?: string | null;
  [key: string]: unknown;
}

export interface PaginatedData {
  items: Broadcast[];
  pageNumber: number;
  totalRows: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class DataRefetchService {
  private readonly errorHandler = inject(ErrorHandlingService);

  handleApiWithRefetch<T, R>(
    apiCall: Observable<ApiResponse<T>>,
    refetchFn: (params: QueryParams) => Observable<R>,
    queryParams: QueryParams,
    calculatePageFn?: (params: QueryParams) => QueryParams
  ): Observable<ApiResponse<T>> {
    return apiCall.pipe(
      switchMap(response => {
        let paramsToUse = { ...queryParams };

        if (calculatePageFn) {
          paramsToUse = calculatePageFn(paramsToUse);
        }
        return refetchFn(paramsToUse).pipe(map(() => response));
      }),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  calculatePageAfterDelete(
    params: QueryParams,
    currentData: PaginatedData | null
  ): QueryParams {
    if (!currentData) {
      return params;
    }

    const { totalRows, pageSize, pageNumber, items } = currentData;

    if (items && items.length === 1 && pageNumber > 1) {
      const totalPages = Math.ceil((totalRows - 1) / pageSize);
      if (pageNumber > totalPages) {
        return { ...params, pageNumber: pageNumber - 1 };
      }
    }

    return params;
  }

  buildHttpParams(queryParams: QueryParams): HttpParams {
    let params = new HttpParams();

    if (queryParams.pageNumber !== undefined) {
      params = params.set('Page', queryParams.pageNumber.toString());
    }

    if (queryParams.pageSize !== undefined) {
      params = params.set('PageSize', queryParams.pageSize.toString());
    }

    if (queryParams.organizationId !== undefined) {
      params = params.set(
        'organizationId',
        queryParams.organizationId.toString()
      );
    }

    if (queryParams.sortField) {
      params = params.set('Sort', queryParams.sortField);
      params = params.set(
        'SortOrder',
        queryParams.sortOrder === 'ascend' ? '0' : '1'
      );
    }

    if (queryParams.searchTerm) {
      params = params.set('SearchTerm', queryParams.searchTerm);
    }

    Object.entries(queryParams).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        ![
          'organizationId',
          'pageNumber',
          'pageSize',
          'sortField',
          'sortOrder',
          'searchTerm',
        ].includes(key)
      ) {
        params = params.set(key, value.toString());
      }
    });

    return params;
  }
}
